import React, {
  useState,
} from "react";

import {
  FiAlertTriangle,
  FiRefreshCw,
} from "react-icons/fi";

import useSiteActivity from "./hooks/useSiteActivity";

import SiteActivityHeader from "./components/SiteActivityHeader";
import SiteActivityStats from "./components/SiteActivityStats";
import SiteActivityPriorityInbox from "./components/SiteActivityPriorityInbox";
import SiteActivityFilters from "./components/SiteActivityFilters";
import SiteActivityCharts from "./components/SiteActivityCharts";
import SiteActivityFamilyTabs from "./components/SiteActivityFamilyTabs";
import SitesActivityTable from "./components/SitesActivityTable";
import ActivityDetailsModal from "./components/ActivityDetailsModal";

import SectionCard from "./ui/SectionCard";

export default function SiteActivityPage() {
  const activity =
    useSiteActivity();

  const [
    details,
    setDetails,
  ] = useState(null);

  const metricsLoading =
    activity.metricsLoadingFamily ===
    activity.activeFamily;

  return (
    <div className="space-y-6">
      {/* ================================================================
          HEADER
          ================================================================ */}

      <SiteActivityHeader
        dateStr={
          activity.dateStr
        }
        setDateStr={
          activity.setDateStr
        }
        refreshing={
          activity.refreshing
        }
        criticalRefreshing={
          activity.criticalRefreshing
        }
        autoRefresh={
          activity.autoRefresh
        }
        setAutoRefresh={
          activity.setAutoRefresh
        }
        lastCriticalRefresh={
          activity.lastCriticalRefresh
        }
        onRefresh={
          activity.refresh
        }
        onExport={
          activity.exportCSV
        }
      />

      {/* ================================================================
          ERREUR GLOBALE
          ================================================================ */}

      {activity.error && (
        <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-200">
          <FiAlertTriangle className="mt-0.5 shrink-0" />

          <div>
            <div className="font-bold">
              Erreur de chargement
            </div>

            <div>
              {activity.error}
            </div>
          </div>
        </div>
      )}

      {/* ================================================================
          KPI
          ================================================================ */}

      <SiteActivityStats
        totals={
          activity.dashboardTotals
        }
        familyLabel={
          activity.activeFamilyLabel
        }
      />

      {/* ================================================================
          BOÎTE DE RÉCEPTION PRIORITAIRE

          Important :
          - tous les messages non lus
          - toutes les requêtes pending
          - toutes les familles confondues
          ================================================================ */}

      <SiteActivityPriorityInbox
        rows={
          activity.rows
        }
        onOpenDetails={
          setDetails
        }
      />

      {/* ================================================================
          FAMILLES
          ================================================================ */}

      <SectionCard
        title="Familles d’applications"
        subtitle="Les messages et requêtes restent surveillés sur l’ensemble du parc. Les statistiques détaillées sont chargées uniquement pour la famille sélectionnée."
      >
        <SiteActivityFamilyTabs
          options={
            activity.familyOptions
          }
          activeFamily={
            activity.activeFamily
          }
          onChange={
            activity.selectFamily
          }
        />
      </SectionCard>

      {/* ================================================================
          GRAPHIQUES
          ================================================================ */}

      <SectionCard
        title={`Vue graphique — ${activity.activeFamilyLabel}`}
        subtitle="Les graphiques concernent uniquement la famille actuellement sélectionnée."
        action={
          metricsLoading ? (
            <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-2 text-xs font-bold text-sky-600 dark:bg-sky-950/40 dark:text-sky-300">
              <FiRefreshCw className="animate-spin" />

              Chargement des
              statistiques…
            </div>
          ) : null
        }
      >
        <SiteActivityCharts
          topSitesData={
            activity.topSitesData
          }
          productChartData={
            activity.productChartData
          }
          familyLabel={
            activity.activeFamilyLabel
          }
          loading={
            metricsLoading
          }
        />
      </SectionCard>

      {/* ================================================================
          TABLEAU
          ================================================================ */}

      <SectionCard
        title={`Sites — ${activity.activeFamilyLabel}`}
        subtitle="Les notifications correspondent à l’activité du jour sélectionné. Les messages non lus et requêtes en attente restent suivis indépendamment de la date."
        action={
          metricsLoading ? (
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-xs font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              <FiRefreshCw className="animate-spin" />

              Mise à jour…
            </div>
          ) : null
        }
      >
        <div className="space-y-5">
          <SiteActivityFilters
            filters={
              activity.filters
            }
            updateFilter={
              activity.updateFilter
            }
            resetFilters={
              activity.resetFilters
            }
            productOptions={
              activity.productOptions
            }
            sortKey={
              activity.sortKey
            }
            sortDir={
              activity.sortDir
            }
            setQuickSortKey={
              activity.setQuickSortKey
            }
          />

          <SitesActivityTable
            rows={
              activity.filteredRows
            }
            totals={
              activity.tableTotals
            }
            loading={
              activity.loading
            }
            sortKey={
              activity.sortKey
            }
            sortDir={
              activity.sortDir
            }
            onSort={
              activity.toggleSort
            }
            onOpenDetails={
              setDetails
            }
          />
        </div>
      </SectionCard>

      {/* ================================================================
          MODALE DÉTAIL
          ================================================================ */}

      <ActivityDetailsModal
        details={details}
        onClose={() =>
          setDetails(null)
        }
      />
    </div>
  );
}